import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Button, Spinner, InputGroup, Alert, Badge } from 'react-bootstrap';
import api from '../../config/roundrobinApi';
import CountdownTimer from './components/CountdownTimer';
import QueueFormModal from './components/QueueFormModal';

export default function QueuesTab() {
  const [queues, setQueues] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [changes, setChanges] = useState({});
  const [message, setMessage] = useState(null);
  const [runningQueues, setRunningQueues] = useState({});

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/queues');
      setQueues(res.data);
      setChanges({});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueues(); }, [fetchQueues]);

  const handleChange = (id, field, value) => {
    setChanges(prev => ({ ...prev, [id]: { ...prev[id], id, [field]: value } }));
  };

  const handleSave = async () => {
    const updates = Object.values(changes);
    if (!updates.length) return;
    setSaving(true);
    try {
      await api.put('/queues/bulk-update', { queues: updates });
      setMessage({ type: 'success', text: 'Saved' });
      fetchQueues();
    } catch (err) { setMessage({ type: 'danger', text: 'Save failed' }); }
    finally { setSaving(false); }
  };

  const handleRunNow = async (queueId) => {
    setRunningQueues(prev => ({ ...prev, [queueId]: true }));
    try {
      const res = await api.post(`/queues/${queueId}/run`);
      setMessage({ type: 'success', text: res.data.message || 'Queue run completed' });
      fetchQueues();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.detail || 'Run failed' });
    }
    finally { setRunningQueues(prev => ({ ...prev, [queueId]: false })); }
  };

  const handleCreated = () => { setShowModal(false); fetchQueues(); };

  const getVal = (queue, field) => {
    const c = changes[queue.id];
    if (c && c[field] !== undefined) return c[field];
    return queue[field];
  };

  const filtered = queues.filter(q => !search || q.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <InputGroup style={{ maxWidth: '300px' }}>
          <Form.Control placeholder="Search queues..." value={search} onChange={e => setSearch(e.target.value)} />
        </InputGroup>
        <div>
          <Button variant="outline-primary" className="me-2" onClick={() => setShowModal(true)}>New</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !Object.keys(changes).length}>
            {saving ? <><Spinner size="sm" className="me-1" />Saving...</> : 'Save'}
          </Button>
        </div>
      </div>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Active</th>
              <th>Next Run</th>
              <th>Name</th>
              <th>Zendesk View</th>
              <th style={{width:'90px'}}>Interval (min)</th>
              <th style={{width:'90px'}}>Limit/Agent</th>
              <th>Comment</th>
              <th style={{width:'110px'}}>Comm. Mode</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(queue => {
              const isDeleted = getVal(queue, 'delete');
              const isRunning = runningQueues[queue.id];
              return (
                <tr key={queue.id} className={isDeleted ? 'text-decoration-line-through text-muted' : ''}>
                  <td><Form.Check type="switch" checked={getVal(queue, 'active')} onChange={e => handleChange(queue.id, 'active', e.target.checked)} /></td>
                  <td><CountdownTimer targetTime={queue.next_run_at} intervalMinutes={queue.run_interval_minutes} /></td>
                  <td>{queue.name}</td>
                  <td><small>{queue.zendesk_view_name || queue.zendesk_view_id}</small></td>
                  <td><Form.Control type="number" size="sm" min={1} value={getVal(queue, 'run_interval_minutes')} onChange={e => handleChange(queue.id, 'run_interval_minutes', parseInt(e.target.value) || 5)} /></td>
                  <td><Form.Control type="number" size="sm" min={0} value={getVal(queue, 'limit_per_agent') ?? ''} onChange={e => handleChange(queue.id, 'limit_per_agent', e.target.value ? parseInt(e.target.value) : null)} /></td>
                  <td><Form.Control type="text" size="sm" value={getVal(queue, 'comment_text') || ''} onChange={e => handleChange(queue.id, 'comment_text', e.target.value)} /></td>
                  <td>
                    <Form.Select size="sm" value={getVal(queue, 'comment_mode')} onChange={e => handleChange(queue.id, 'comment_mode', e.target.value)}>
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                    </Form.Select>
                  </td>
                  <td>
                    <small>
                      {queue.last_run_status ? (
                        <Badge bg={queue.last_run_status.startsWith('success') ? 'success' : 'danger'} className="fw-normal">
                          {queue.last_run_status}
                        </Badge>
                      ) : <span className="text-muted">-</span>}
                    </small>
                  </td>
                  <td className="text-nowrap">
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-1"
                      disabled={!queue.active || isRunning}
                      onClick={() => handleRunNow(queue.id)}
                      title={!queue.active ? 'Queue is inactive' : 'Run this queue now'}
                    >
                      {isRunning ? <Spinner size="sm" /> : 'Run Now'}
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleChange(queue.id, 'delete', !isDeleted)}>&#x2715;</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <QueueFormModal show={showModal} onHide={() => setShowModal(false)} onCreated={handleCreated} />
    </>
  );
}
